import { useEffect, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";

export enum FeatureFlag {
  GrowUpAllowContracting = "allowContracting",
  AnalysisSharing = "analysisSharing",
  Duplicate = "duplicate",
  CreateMangoClient = "createMangoClient",
  BudgetBulkEntry = "budgetBulkEntry",
  MinorClient = "minorClient",
}

// every mounted useFeatureFlag has to re-render when a flag is toggled (e.g. from DevTools), not only on remount
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

export const getFeatureFlagKey = (flag: FeatureFlag) => `ff_${flag}`;

const getIsFeatureFlagStored = (flag: FeatureFlag): boolean => {
  try {
    return localStorage.getItem(getFeatureFlagKey(flag)) === "true";
  } catch {
    return false;
  }
};

export const setFeatureFlagStored = (flag: FeatureFlag, isEnabled: boolean) => {
  const key = getFeatureFlagKey(flag);
  try {
    if (isEnabled) {
      localStorage.setItem(key, "true");
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Failed to save to localStorage for key "${key}":`, error);
  }
  for (const listener of listeners) {
    listener();
  }
};

export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  const [searchParams] = useSearchParams();
  const isStored = useSyncExternalStore(subscribe, () => getIsFeatureFlagStored(flag));
  const isInUrl = searchParams.has(getFeatureFlagKey(flag));

  useEffect(() => {
    if (isInUrl && !isStored) {
      setFeatureFlagStored(flag, true);
    }
  }, [flag, isInUrl, isStored]);

  return isInUrl || isStored;
};
